import { useSyncExternalStore } from "react";
import { AuthenticationView } from "../types";
import { CodeISO2 } from "@mindlercare/schema";

type HistoryStateItem = {
  view: AuthenticationView;
  market: CodeISO2;
};

const getCurrentState = (): HistoryStateItem | null => {
  // We store hash in state instead of URL because we cannot really clean history stack
  // at least olehlutsenko have found a way, so it leaves one ugly hash in history after login
  // so this is second best option.
  return window.history.state?.authUI ?? null;
};

const subscribe = (callback: () => void) => {
  window.addEventListener("popstate", callback);
  window.addEventListener("pushHistoryState", callback);

  return () => {
    window.removeEventListener("popstate", callback);
    window.removeEventListener("pushHistoryState", callback);
  };
};

const navigate = (state: HistoryStateItem) => {
  // we want to preserve current state, which can be set via react-router
  window.history.pushState(
    {
      ...window.history.state,
      authUI: state,
    },
    "",
  );
  // to support forward navigation, we need custom event
  window.dispatchEvent(new Event("pushHistoryState"));
};

const replace = (state: HistoryStateItem) => {
  // we want to preserve current state, which can be set via react-router
  window.history.replaceState(
    {
      ...window.history.state,
      authUI: state,
    },
    "",
  );
  // to support forward navigation, we need custom event
  window.dispatchEvent(new Event("pushHistoryState"));
};

const goBack = () => {
  window.history.back();
};

const goForward = () => {
  window.history.forward();
};

/**
 * A simple state-based navigation hook that allows navigating between views
 * where each view is just a state in history stack. It allows it not to interfere with
 * libraries like react-router.
 *
 * @returns An object with the current state and navigation functions
 */
export const useStateNavigator = () => {
  const currentState = useSyncExternalStore(subscribe, getCurrentState);

  return {
    currentState,
    navigate,
    goBack,
    goForward,
    replace,
  };
};
